<template>
  <div class="sidebar-more-popover">
    <TirPmPopover
      ref="$popover"
      :width="width"
      :position="position"
      @close="onClosePopover"
      @click="onOpenPopover"
    >
      <slot name="target" />
      <template #content>
        <template v-if="viewRenameForm">
          <SidebarEditPopoverFolderForm
            v-if="groupItem"
            :group="groupItem"
            @on-update="onUpdateGroup"
          />
          <SidebarEditPopoverQueueForm
            v-else-if="queueItem"
            :queue="queueItem"
            @on-update="onUpdateQueue"
          />
          <SidebarRenamePopoverNamespaceForm
            v-else-if="namespaceItem"
            :namespace="namespaceItem"
            @on-update="onUpdateNamespace"
          />
        </template>
        <PopoverOptionList v-else>
          <PopoverOptionLi @click.stop="openRenameForm">
            <template #left-icon>
              <PencilSquareIcon />
            </template>
            Переименовать
          </PopoverOptionLi>
          <PopoverOptionLi
            v-if="[TreeItemType.Queue, TreeItemType.Namespace].includes(menuItem.Type)"
            @click="edit"
          >
            <template #left-icon>
              <AdjustmentsVerticalIcon />
            </template>
            Настройки
          </PopoverOptionLi>
          <PopoverOptionLi @click.stop="openDeleteConfirmModal">
            <template #left-icon>
              <TrashIcon />
            </template>
            Удалить
          </PopoverOptionLi>
        </PopoverOptionList>
      </template>
    </TirPmPopover>

    <TirPmModal
      :is-visible="deleteConfirmModalVisible"
      :is-with-back="false"
      :title="deleteConfirmModalMessage"
      @close="cancelDeleteConfrimModal"
    >
      <template #footer>
        <TirPmButton
          style="width: 6.5rem"
          :size="TirPmButtonSizeEnum.Regular"
          :variant="TirPmButtonVariantEnum.PrimaryState"
          @click="cancelDeleteConfrimModal"
        >
          Нет
        </TirPmButton>
        <TirPmButton
          style="width: 6.5rem"
          :size="TirPmButtonSizeEnum.Regular"
          :variant="TirPmButtonVariantEnum.SecondaryState"
          @click="deleteMenuItem"
        >
          Да
        </TirPmButton>
      </template>
    </TirPmModal>
  </div>
</template>
<script lang="ts" setup>
import { computed, PropType, ref } from "vue";
import {
  TirPmModal,
  TirPmPopover,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  PopoverPositionEnum,
} from "tir-components";
import { PopoverOptionList, PopoverOptionLi } from "../popover-option-list";
import {
  PencilSquareIcon,
  AdjustmentsVerticalIcon,
  TrashIcon,
} from "tir-style-system/icons/outline";
import { NamespaceModel, GroupModel, QueueTasksModel } from "../../models";
import { TreeItemType } from "../../enums";
import { AdapterService } from "../../adapter";
import SidebarEditPopoverFolderForm from "../sidebar-edit-popover-folder-form.vue";
import SidebarEditPopoverQueueForm from "../sidebar-edit-popover-queue-form.vue";
import SidebarRenamePopoverNamespaceForm from "../sidebar-rename-popover-namespace-form.vue";
import { QueueCardtemModel } from "@/components/queue-card";
import { useRouter } from "vue-router";

const router = useRouter();

const adapter = new AdapterService();

const emit = defineEmits<{
  /**Событие при удалении неймспейса */
  (e: "onDeleteNamespace", namespaceId: string): void;
  /**Событие при удалении группы */
  (e: "onDeleteGroup", groupId: number): void;
  /**Событие при удалении очереди */
  (e: "onDeleteQueue", queueId: string): void;
  /**Событие при обновлении группы */
  (e: "onUpdateGroup", group: GroupModel): void;
  /**Событие при обновлении очереди */
  (e: "onUpdateQueue", queue: QueueTasksModel): void;
  /**Событие обновления неймспейса */
  (e: "onUpdateNamespace", namespace: NamespaceModel): void;
  /**Событие смены видимости поповера */
  (e: "toggleVisible", value: boolean): void;
  /**Редактирование очереди */
  (e: "editQueue"): void;
}>();

const props = defineProps({
  /**Элемент меню */
  menuItem: {
    type: Object as PropType<NamespaceModel | GroupModel | QueueTasksModel | QueueCardtemModel>,
    required: true,
  },
  position: {
    type: String as PropType<PopoverPositionEnum>,
    default: PopoverPositionEnum.Auto,
  },
});
let owner = false;

/**Элемент рабочей области */
const namespaceItem = computed(() => (props.menuItem.isNamespace() ? props.menuItem : undefined));
/**Элемент группы */
const groupItem = computed(() => (props.menuItem.isGroup() ? props.menuItem : undefined));
/**Элемент очереди задач */
const queueItem = computed(() => (props.menuItem.isQueue() ? props.menuItem : undefined));

/**Ссылка на компонент поповера */
const $popover = ref<InstanceType<typeof TirPmPopover>>();

/**Отобразить форму переименования */
const viewRenameForm = ref(false);

/**Ширина поповера */
const width = computed(() => {
  if (viewRenameForm.value) return "25rem";
  return "17.5rem";
});

/**
 * * При открытии поповера
 */
const onOpenPopover = () => {
  emit("toggleVisible", true);
};

/**
 * * При закрытии поповера
 */
const onClosePopover = () => {
  viewRenameForm.value = false;
  if (!deleteConfirmModalVisible.value) emit("toggleVisible", false);
};

/**Видимость модального окна с подтверждением удаления */
const deleteConfirmModalVisible = ref(false);
/**Сообщение для модального окна подтверждения удаления */
const deleteConfirmModalMessage = ref("");

/**Открыть форму переименования */
const openRenameForm = () => {
  viewRenameForm.value = true;
  $popover.value?.init(true);
};

/**Открыть модальное окно подтверждения удаления */
const openDeleteConfirmModal = () => {
  switch (true) {
    case props.menuItem.isNamespace():
      deleteConfirmModalMessage.value = `Вы дейтсвительно хотите удалить раздел "${props.menuItem.Name}"?`;
      break;
    case props.menuItem.isGroup():
      deleteConfirmModalMessage.value = `Вы дейтсвительно хотите удалить папку "${props.menuItem.Name}"?`;
      break;
    case props.menuItem.isQueue():
      deleteConfirmModalMessage.value = `Вы дейтсвительно хотите удалить "${props.menuItem.Name}"?`;
    default:
      break;
  }
  deleteConfirmModalVisible.value = true;
  $popover.value?.close();
};

/**Закрыть модальное окно подтверждения удаления */
const closeDeleteConfirmModal = () => {
  deleteConfirmModalVisible.value = false;
  deleteConfirmModalMessage.value = "";
  emit("toggleVisible", false);
};

/**Отмена удаления в модальном окне подтверждения */
const cancelDeleteConfrimModal = async () => {
  closeDeleteConfirmModal();
  setTimeout(() => {
    $popover.value?.open();
  });
};

/**Удалить пункт меню */
const deleteMenuItem = async () => {
  const menuItem = props.menuItem;

  if (menuItem.isNamespace()) {
    const resp = await adapter.deleteNamespace(menuItem.Id, menuItem.Name);
    if (resp.IsSuccess) {
      closeDeleteConfirmModal();
      emit("onDeleteNamespace", menuItem.Id);
    }
  } else if (menuItem.isGroup()) {
    const resp = await adapter.deleteGroup(menuItem.Id, menuItem.Name);
    if (resp.IsSuccess) {
      closeDeleteConfirmModal();
      emit("onDeleteGroup", menuItem.Id);
    }
  } else if (menuItem.isQueue()) {
    const resp = await adapter.deleteQueue(menuItem.Id, menuItem.Name);
    if (resp.IsSuccess) {
      closeDeleteConfirmModal();
      emit("onDeleteQueue", menuItem.Id);
    }
  }

  closeDeleteConfirmModal();
};

/**
 * * При обновлении группы
 * @param group Группа
 */
const onUpdateGroup = (group: GroupModel) => {
  emit("onUpdateGroup", group);
  $popover.value?.close();
};

/**
 * * При обновлении очереди
 * @param queue Очередь
 */
const onUpdateQueue = (queue: QueueTasksModel) => {
  emit("onUpdateQueue", queue);
  $popover.value?.close();
};

/**
 * * При обновлении неймспейса
 * @param namespace Неймспейс
 */
const onUpdateNamespace = (namespace: NamespaceModel) => {
  emit("onUpdateNamespace", namespace);
  $popover.value?.close();
};

const edit = () => {
  $popover.value?.close();
  if (props.menuItem.isQueue()) {
    emit("editQueue");
  } else if (props.menuItem.isNamespace()) {
    router.push({
      name: "namespace-settings",
      params: {
        namespaceId: props.menuItem.Id,
      },
    });
  }
};
defineExpose({
  edit,
});
</script>
<style lang="scss" scoped>
.sidebar-more-popover {
  display: flex;
}
</style>
